import {
  featureRequestCreateResponseSchema,
  featureRequestListResponseSchema,
  feedbackStatusResponseSchema,
  type FeatureRequest,
  type FeedbackStatusResponse,
} from '@awwyeah/shared';

/**
 * REST client for the "Ideas for AwwYeah?" box (`/api/feedback`). Session-authed, same
 * `credentials: 'include'` convention as `mcp/connectorApi.ts`.
 */

export async function fetchFeedbackStatus(): Promise<FeedbackStatusResponse> {
  const res = await fetch('/api/feedback/status', { credentials: 'include' });
  if (!res.ok) throw new Error(`feedback status failed: ${res.status}`);
  return feedbackStatusResponseSchema.parse(await res.json());
}

/**
 * Sends an idea. Throws an `Error` whose message is ready to show the sender — the caller
 * keeps their text in the box either way, so nothing typed is ever lost.
 */
export async function sendFeatureRequest(text: string, screen: string): Promise<FeatureRequest> {
  let res: Response;
  try {
    res = await fetch('/api/feedback', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ text, screen }),
    });
  } catch {
    throw new Error("Didn't send — looks like you're offline. Your note is still here.");
  }
  if (!res.ok) {
    throw new Error(
      res.status === 400
        ? 'That one is too long to send — trim it a little and try again.'
        : "Didn't send — something hiccuped. Your note is still here; try again in a bit.",
    );
  }
  return featureRequestCreateResponseSchema.parse(await res.json()).request;
}

export async function fetchFeatureRequests(): Promise<FeatureRequest[]> {
  const res = await fetch('/api/feedback', { credentials: 'include' });
  if (!res.ok) throw new Error(`feature requests failed: ${res.status}`);
  return featureRequestListResponseSchema.parse(await res.json()).requests;
}
